import React, { useState } from 'react';
import { 
  Box, Typography, Switch, Button, FormControlLabel, Chip, Menu, MenuItem, 
  IconButton, Divider, ListItemIcon, ListItemText, Tooltip, Popover,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@mui/material';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
// import HistoryIcon from '@mui/icons-material/History'; // History/Recent searches not implemented in hook yet
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { SavedSearch, ServiceDefinition } from '../../models';
import { useSearchContext } from '../../contexts/SearchContext';
import { useDialogContext } from '../../contexts/DialogContext';

// Helper function to format hours display
const formatHours = (minutes: number): string => {
  const hours = minutes / 60;
  const formatted = hours.toFixed(1);
  // Remove trailing .0 for whole numbers
  return formatted.endsWith('.0') ? formatted.substring(0, formatted.length - 2) : formatted;
};

/**
 * A persistent bar showing the current search context and providing search management options
 * Uses the enhanced SearchContext for state and actions.
 */
const SearchContextBar: React.FC = () => {
  // Get all needed data and functions from the enhanced SearchContext
  const {
    searchParams,
    savedSearches,
    currentSavedSearch,
    isLoading,
    error,
    loadSearch,
    deleteSearch,
    toggleAutoSave,
    clearLoadedSearch
  } = useSearchContext();
  // Get dialog context setters
  const { setSaveSearchDialogOpen, setShowSavedSearches } = useDialogContext();

  // Extract values from searchParams with defaults
  const selectedLocationName = searchParams?.location?.name || 'No location';
  const selectedDate = searchParams?.dateOfInterest?.toISOString() || new Date().toISOString();
  const services = searchParams?.services || [];
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  
  // Popover state for search details tooltip
  const [detailsAnchorEl, setDetailsAnchorEl] = useState<HTMLElement | null>(null);
  const [activeSearch, setActiveSearch] = useState<SavedSearch | null>(null);
  
  // Confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [searchToDelete, setSearchToDelete] = useState<SavedSearch | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    handleHideDetails(); // Ensure tooltips are cleared when menu is closed
  };

  // Show search details on hover
  const handleShowDetails = (event: React.MouseEvent<HTMLElement>, search: SavedSearch) => {
    setDetailsAnchorEl(event.currentTarget);
    setActiveSearch(search);
  };
  
  const handleHideDetails = () => {
    setDetailsAnchorEl(null);
    setActiveSearch(null);
  };
  
  // Delete search handling
  const handleDeleteClick = (event: React.MouseEvent, search: SavedSearch) => {
    event.stopPropagation(); // Prevent triggering the parent MenuItem click
    setSearchToDelete(search);
    setDeleteDialogOpen(true);
  };
  
  const handleConfirmDelete = () => {
    if (searchToDelete) {
      deleteSearch(searchToDelete.id);
      setDeleteDialogOpen(false);
      setSearchToDelete(null);
    }
  };
  
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setSearchToDelete(null);
  };

  // Format date as month/year
  const formatMonthYear = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  };
  
  // For popover - details of search
  const detailsOpen = Boolean(detailsAnchorEl);
  const popoverId = detailsOpen ? 'search-details-popover' : undefined;

  return (
    <Box 
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 1.5,
        mb: 2,
        bgcolor: 'background.paper',
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
        {currentSavedSearch ? (
          <>
            <BookmarkIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="subtitle1" fontWeight="medium" sx={{ mr: 2 }}>
              {currentSavedSearch.name}
            </Typography>
          </>
        ) : (
          <Typography variant="subtitle1" color="text.secondary">
            Unsaved Search
          </Typography>
        )}
        
        <Divider orientation="vertical" flexItem sx={{ mx: 1.5 }} />
        
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Location with icon (not a chip) */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            mr: 1.5
          }}>
            <LocationOnIcon fontSize="small" color="primary" sx={{ mr: 0.5 }} />
            <Typography variant="body1">
              {selectedLocationName || 'No location'}
            </Typography>
          </Box>
          
          {/* Service chips with tooltips */}
          {services.map((service) => {
            // Get color directly from service definition with strict error handling
            if (!service.color) {
              console.error(`Service missing required color property: ${service.id}`);
              throw new Error(`Data integrity error: Service ${service.name} (${service.id}) is missing required color property`);
            }
            const serviceColor = service.color;
            return (
              <Tooltip
                key={service.id}
                title={
                  <Box sx={{ p: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      {service.name}
                    </Typography>
                    
                    <Typography variant="body2" gutterBottom>
                      <strong>Duration:</strong> {formatHours(service.durationMinutes)} {service.durationMinutes / 60 === 1 ? 'hour' : 'hours'}
                    </Typography>
                    
                    <Typography variant="body2" sx={{ mt: 1 }} gutterBottom>
                      <strong>Tide Restrictions:</strong>
                    </Typography>
                    {service.tideRestrictions.launchRequired && (
                      <Typography variant="body2" sx={{ ml: 2 }}>
                        • Launch min height: {service.tideRestrictions.launchMinHeight}m
                      </Typography>
                    )}
                    {service.tideRestrictions.retrievalRequired && (
                      <Typography variant="body2" sx={{ ml: 2 }}>
                        • Retrieval min height: {service.tideRestrictions.retrievalMinHeight}m
                      </Typography>
                    )}
                    
                    <Typography variant="body2" sx={{ mt: 1 }} gutterBottom>
                      <strong>Time Restrictions:</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      • Earliest start: {service.timeRestrictions.earliestStart}
                    </Typography>
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      • Latest start: {service.timeRestrictions.latestStart}
                    </Typography>
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      • Latest end: {service.timeRestrictions.latestEnd}
                    </Typography>
                  </Box>
                }
                arrow
              >
                <Chip
                  label={service.name}
                  size="small"
                  sx={{
                    backgroundColor: serviceColor,
                    color: 'white',
                    '&:hover': {
                      backgroundColor: serviceColor,
                      opacity: 0.9
                    }
                  }}
                />
              </Tooltip>
            );
          })}
        </Box>
      </Box>
      
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {!currentSavedSearch && (
          <Button
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={() => setSaveSearchDialogOpen(true)}
            size="small"
            sx={{ mr: 1 }}
            disabled={isLoading}
          >
            Save
          </Button>
        )}
        
        {currentSavedSearch && (
          <Tooltip title={currentSavedSearch.autoSave ? "Auto-save is enabled" : "Auto-save is disabled"}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!currentSavedSearch?.autoSave}
                  onChange={() => currentSavedSearch && toggleAutoSave(currentSavedSearch.id)}
                  color="primary"
                  size="small"
                  disabled={!currentSavedSearch || isLoading}
                />
              }
              label="Auto-save"
              sx={{ mr: 1 }}
            />
          </Tooltip>
        )}
        
        <Button
          id="searches-menu-button"
          aria-controls={open ? 'searches-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          variant="contained"
          endIcon={<ArrowDropDownIcon />}
          size="small"
        >
          Searches
        </Button>
        
        {/* Create menu items array before rendering */}
        {(() => {
          // Initialize menu items array
          const menuItems: React.ReactNode[] = [];

          // Add clear current search item if a search is loaded
          if (currentSavedSearch) {
            menuItems.push(
              <MenuItem key="clear" onClick={() => {
                handleHideDetails();
                clearLoadedSearch();
                handleClose();
              }} disabled={isLoading}>
                <ListItemIcon>
                  <AutorenewIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Clear current search</ListItemText>
              </MenuItem>
            );
          }
          
          // Add save as new search item
          menuItems.push(
            <MenuItem key="save" onClick={() => {
              handleHideDetails();
              setSaveSearchDialogOpen(true);
              handleClose();
            }} disabled={isLoading}>
              <ListItemIcon>
                <SaveIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Save as new search</ListItemText>
            </MenuItem>
          );
          
          // Add saved searches section
          if (savedSearches.length > 0) {
            menuItems.push(<Divider key="saved-divider" />);
            menuItems.push(
              <MenuItem key="saved-header" disabled>
                <Typography variant="body2" color="text.secondary">
                  Saved Searches
                </Typography>
              </MenuItem>
            );
            
            // Add saved search items (limited to 5)
            savedSearches.slice(0, 5).forEach((search) => {
              menuItems.push(
                <MenuItem 
                  key={`saved-${search.id}`}
                  onMouseEnter={(e) => handleShowDetails(e, search)}
                  onMouseLeave={handleHideDetails}
                  onClick={() => {
                      loadSearch(search.id);
                      handleClose();
                  }}
                  disabled={isLoading}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pr: 1
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ListItemIcon>
                      <BookmarkIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={search.name} />
                  </Box>
                  <IconButton
                    size="small"
                    edge="end"
                      color="error"
                      onClick={(e) => handleDeleteClick(e, search)}
                      sx={{ ml: 1, opacity: 0.7 }}
                    disabled={isLoading}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </MenuItem>
              );
            });
            
            // Add "view all" item if there are more than 5 saved searches
            if (savedSearches.length > 5) {
              menuItems.push(
                <MenuItem key="view-all" onClick={() => {
                  handleHideDetails();
                  setShowSavedSearches(true);
                  handleClose();
                }} disabled={isLoading}>
                  <ListItemText
                    primary={`View all (${savedSearches.length})`}
                    primaryTypographyProps={{ align: 'center', color: 'primary' }}
                  />
                </MenuItem>
              );
            }
          }
          
          return (
            <Menu
              id="searches-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              MenuListProps={{
                'aria-labelledby': 'searches-menu-button',
              }}
            >
              {menuItems}
            </Menu>
          );
        })()}
        
        {/* Search Details Popover */}
        <Popover
          id={popoverId}
          open={detailsOpen}
          anchorEl={detailsAnchorEl}
          onClose={handleHideDetails}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          sx={{ pointerEvents: 'none' }}
        >
          {activeSearch && (
            <Box sx={{ p: 2, maxWidth: 300 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                {activeSearch.name}
              </Typography>
              {/* Access parameters */}
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Location ID:</strong> {activeSearch.parameters.locationId}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Date:</strong> {formatMonthYear(activeSearch.parameters.dateOfInterest)}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Services:</strong> {activeSearch.parameters.services.length} services
              </Typography>
              <Box sx={{ mt: 1 }}>
                {activeSearch.parameters.services.map((service, idx) => (
                  <Chip
                    key={idx} // Using index as key is okay here as the list is static within the popover
                    label={service.name}
                    size="small"
                    sx={{ 
                      mr: 0.5, 
                      mb: 0.5,
                      backgroundColor: service.color,
                      color: 'white'
                    }}
                  />
                ))}
              </Box>
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                Auto-save: {activeSearch.autoSave ? 'Enabled' : 'Disabled'}
              </Typography>
            </Box>
          )}
        </Popover>
        
        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleCancelDelete}
        >
          <DialogTitle>
            Delete Saved Search
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete "{searchToDelete?.name}"? 
              This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmDelete} 
              color="error" 
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default SearchContextBar;
